import { Autocomplete, FormControlLabel, Grid, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import { sortBy } from "lodash";
import React, { useMemo } from "react";
import { t } from "ttag";
import { useStoreState } from "../../../models/RootStore";
import { PackageCheckbox } from "./PackageTrees";
import useStyles from "./SiteSetup.style";
import { getOptionLabel, isSameOption } from "./siteSetupForm";

// Setup form, top: pick an existing customer, or name a new one (optionally with its customer admin user)
const CustomerSection = () => {
  const classes: any = useStyles();
  const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched }: any =
    useFormikContext<any>();
  const customers = useStoreState((state) => state.customers.customersBasicList);

  const customerOptions = useMemo(
    () => sortBy(
      Object.values(customers).map((customer: any) => ({ value: customer.id, label: customer.name || "" })),
      (option: any) => option.label.toUpperCase()
    ),
    [customers]
  );

  const handleCustomerChange = (event: any, option: any) => {
    setFieldValue("customerId", option?.value || "");
  };

  const handleCustomerBlur = () => {
    setFieldTouched("customerId", true);
  };

  // Helper text falls back to " " so the field keeps its message line and nothing shifts when an error shows
  const fieldError = (name: any): any => (touched[name] && errors[name]) || "";

  const renderCustomerInput = (params: any) => (
    <TextField
      {...params}
      label={t`Customer`}
      margin="none"
      error={!!fieldError("customerId")}
      helperText={fieldError("customerId") || " "}
      FormHelperTextProps={{ className: classes.helperText }}
      className={classes.field}
    />
  );

  return (
    <>
      <Typography variant="subtitle1" gutterBottom className={classes.mainSectionTitle}>{t`Customer`}</Typography>
      <RadioGroup row name="customerMode" value={values.customerMode} onChange={handleChange}>
        <FormControlLabel value="existing" control={<Radio />} label={t`Existing customer`} />
        <FormControlLabel value="new" control={<Radio />} label={t`New customer`} />
      </RadioGroup>

      <Grid container columnSpacing={2} alignItems="flex-start">
        <Grid item xs={12} md={5}>
          {values.customerMode === "existing" ? (
            <Autocomplete
              size="small"
              options={customerOptions}
              value={customerOptions.find((option: any) => option.value === values.customerId) || null}
              onChange={handleCustomerChange}
              onBlur={handleCustomerBlur}
              getOptionLabel={getOptionLabel}
              isOptionEqualToValue={isSameOption}
              renderInput={renderCustomerInput}
            />
          ) : (
            <TextField
              fullWidth
              margin="none"
              size="small"
              name="newCustomerName"
              label={t`New customer name`}
              value={values.newCustomerName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!fieldError("newCustomerName")}
              helperText={fieldError("newCustomerName") || " "}
              FormHelperTextProps={{ className: classes.helperText }}
              className={classes.field}
            />
          )}
        </Grid>
        {values.customerMode === "new" && (
          <Grid item xs={12} md={7}>
            <div className={classes.customerAdminUserRow}>
              <FormControlLabel
                className={classes.packageCheckboxLabel}
                control={
                  <PackageCheckbox
                    name="createCustomerAdminUser"
                    checked={values.createCustomerAdminUser}
                    onChange={handleChange}
                  />
                }
                label={t`Create default customer admin user`}
              />
            </div>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default CustomerSection;
